import {
  type LangMap,
  getPreferredLangs,
  resolveLangMapValue,
} from 'src/app/shared/utils/lang';

import { Injectable, inject } from '@angular/core';

import { take } from 'rxjs/operators';

import { TranslateService } from '@ngx-translate/core';

import { CsvService } from './csv.service';
import type { DataCollectionRequest } from './data.service';
import { DataService } from './data.service';
import { FireStoreService } from './fire-store.service';
import type { ExcelSheet } from './xlsx.service';
import { XlsxService } from './xlsx.service';

import {
  isFsV1Data,
  isFsV2DataCampaign,
  isFsV2DataSpot,
} from './firestore-type-guards';

import type {
  CampaignDataItem,
  DailyAccessDto,
} from '../repositories/dto/datareport.dto';
import {
  type TableDataHeader,
  type TableHeaderLabels,
  getTableHeaders,
} from './table-headers.config';

interface AccessData {
  pv?: Record<string, number>;
  pv_iframe?: Record<string, number>;
  uu?: Record<string, number>;
  uu_iframe?: Record<string, number>;
  ss?: Record<string, number>;
  ss_iframe?: Record<string, number>;
}

interface FieldMapping {
  data: AccessData;
  property: keyof AccessData;
}

@Injectable({
  providedIn: 'root',
})
export class TableDataExportService {
  // Inject services
  private readonly dataService = inject(DataService);
  private readonly fsService = inject(FireStoreService);
  private readonly csvService = inject(CsvService);
  private readonly xlsxService = inject(XlsxService);
  private readonly translate = inject(TranslateService);

  /** 翻訳キーとラベルフィールドのマッピング */
  private static readonly LABEL_KEYS = [
    'date',
    'pv',
    'pvIframe',
    'pvInMap',
    'pvInMapIframe',
    'user',
    'userIframe',
    'userInMap',
    'userInMapIframe',
    'session',
    'sessionIframe',
    'sessionInMapIframe',
    'sessionInMap',
    'spotName',
    'spotClick',
    'spotClickInMap',
    'referrer',
    'campaignName',
    'campaignSource',
    'campaignMedium',
    'language',
    'device',
    'area',
    'os',
  ] as const;

  labels: Record<(typeof TableDataExportService.LABEL_KEYS)[number], string> =
    Object.fromEntries(
      TableDataExportService.LABEL_KEYS.map((key) => [key, '']),
    ) as Record<(typeof TableDataExportService.LABEL_KEYS)[number], string>;

  constructor() {
    const translateKeys = TableDataExportService.LABEL_KEYS.map(
      (key) => `dataExport.${key}`,
    );
    this.translate.get([...translateKeys]).subscribe((translations) => {
      for (const key of TableDataExportService.LABEL_KEYS) {
        this.labels[key] = translations[`dataExport.${key}`];
      }
    });
  }

  /**
   * DataService から複数のデータを取得して、テーブル形式に変換＆エクスポートします。
   * @param collections DataService の getCombinedStream で要求するコレクション一覧
   * @param fileName 出力ファイル名（CSVの場合はコレクション名ごとに別ファイル、XLSXの場合はシート名がコレクション名）
   * @param fileType csv or xlsx
   */
  public exportData(
    collections: DataCollectionRequest[],
    fileName: string,
    fileType: 'csv' | 'xlsx',
  ): void {
    this.dataService
      .getCombinedStream(collections)
      .pipe(take(1))
      .subscribe(async (streamData) => {
        // streamDataは [ { collection, current, comparison }, ... ] の配列

        switch (fileType) {
          case 'csv':
            {
              // collectionごとにCSVファイルを作成し、zipでまとめる
              const csvFiles: { [filename: string]: string[][] } = {};
              for (const item of streamData) {
                const tableData = await this.convertToTableData(
                  item.collection,
                  item.current as DailyAccessDto,
                );

                // CSV ファイル出力 (ファイル名に collection.name を付ける)
                const csvFileName = `${fileName}_${
                  item.collection.sheetName || item.collection.name
                }`;
                csvFiles[csvFileName] = tableData;
              }
              await this.csvService.exportChartDataAsZip(
                csvFiles,
                `${fileName}.zip`,
              );
            }
            break;

          case 'xlsx':
            {
              const excelSheets: ExcelSheet[] = [];

              // collectionごとにsheetを作成し、1つのxlsxファイルにまとめる
              for (const item of streamData) {
                const tableData = await this.convertToTableData(
                  item.collection,
                  item.current as DailyAccessDto,
                );

                const sheetName = `${
                  item.collection.sheetName || item.collection.name
                }`;
                excelSheets.push({
                  sheetName,
                  data: tableData,
                });
              }

              this.xlsxService.exportExcel(excelSheets, fileName);
            }
            break;
          default:
            console.error('Unsupported file type:', fileType);
        }
      });
  }

  private getTableHeaders(
    collection: DataCollectionRequest,
  ): TableDataHeader[] {
    return getTableHeaders(collection, this.labels as TableHeaderLabels);
  }

  /**
   * 指定されたデータ配列を2次元配列に変換し、先頭行にヘッダーを設定。
   */
  private async convertToTableData(
    collection: DataCollectionRequest,
    data: DailyAccessDto,
  ): Promise<string[][]> {
    if (!data) {
      return [];
    }

    const headers = this.getTableHeaders(collection);
    const header_names: string[] = headers.map(
      (header: { field: string; name: string }) => header.name,
    );

    const tableData: string[][] = [];
    // ヘッダー行を先頭に追加
    tableData.push(header_names);

    // TODO: データの種類ごとに適切な処理をする
    // console.group('Convert to Table Data');
    // console.log('data', data);

    const isDaily = collection.isDaily;
    if (isDaily) {
      tableData.push(...this.dailyDataToArray(data, headers));
    } else {
      const res = await this.nonDailyDataToArray(data, headers);
      tableData.push(...res);
    }

    // console.log('tableData', tableData);
    // console.groupEnd();

    return tableData;
  }

  /**
   * 日別データを2次元配列に変換する
   */
  private dailyDataToArray(
    dailyData: DailyAccessDto,
    headers: TableDataHeader[],
  ): string[][] {
    const { allarea, inmap } = dailyData;

    const dateLabels = Object.keys(allarea.pv);

    // 日付のソート
    dateLabels.sort((a, b) => {
      const dateA = new Date(a);
      const dateB = new Date(b);
      return dateA.getTime() - dateB.getTime();
    });

    // フィールドとデータのマッピング定義
    const fieldMapping: Record<string, FieldMapping> = {
      // ページ閲覧数
      pv: { data: allarea, property: 'pv' },
      // 埋込みマップ閲覧数(ページ閲覧数)
      pv_iframe: { data: allarea, property: 'pv_iframe' },
      // ユーザー数
      uu: { data: allarea, property: 'uu' },
      // 埋込みマップ閲覧数(ユーザー数)
      uu_iframe: { data: allarea, property: 'uu_iframe' },
      // セッション数
      session: { data: allarea, property: 'ss' },
      // 埋込みマップ閲覧数(セッション数)
      session_iframe: { data: allarea, property: 'ss_iframe' },
      // 位置情報ON利用者数(ページ閲覧数)
      inmap_pv: { data: inmap, property: 'pv' },
      // 位置情報ON利用者数(ユーザー数)
      inmap_uu: { data: inmap, property: 'uu' },
      // 埋込みマップ閲覧数(位置情報ON利用者数)(ページ閲覧数)
      inmap_pv_iframe: { data: inmap, property: 'pv_iframe' },
      // 埋込みマップ閲覧数(位置情報ON利用者数)(ユーザー数)
      inmap_uu_iframe: { data: inmap, property: 'uu_iframe' },
      // 位置情報ON利用者数(セッション数)
      inmap_session: { data: inmap, property: 'ss' },
      // 埋込みマップ閲覧数(位置情報ON利用者数)(セッション数)
      inmap_session_iframe: { data: inmap, property: 'ss_iframe' },
      // 埋込みマップ閲覧数(位置情報ON利用者数)(ユーザー数) - 重複フィールド
      inmap_uu_iframe_2: { data: inmap, property: 'uu_iframe' },
      // 埋込みマップ閲覧数(位置情報ON利用者数)(セッション数) - 重複フィールド
      inmap_session_iframe_2: { data: inmap, property: 'ss_iframe' },
    };

    const results = dateLabels.map((date) => {
      const row = [date];

      // ヘッダー定義に基づいてデータを構築
      for (let i = 1; i < headers.length; i++) {
        const field = headers[i].field;
        const mapping = fieldMapping[field];

        if (mapping) {
          const { data, property } = mapping;
          const value = data[property]?.[date] || 0;
          row.push(String(value));
        } else {
          row.push('0');
        }
      }

      return row;
    });

    return results;
  }

  private async nonDailyDataToArray(
    data: DailyAccessDto,
    headers: TableDataHeader[],
  ): Promise<string[][]> {
    // console.log('nonDailyDataToArray', data);
    // allareaの直下に、pv, pv_iframe, uu, uu_iframeをもつ形式
    if (isFsV1Data(data)) {
      const { allarea, inmap } = data;

      const hasPvIframe = headers.some(
        (header) => header.field === 'pv_iframe',
      );
      const hasUu = headers.some((header) => header.field === 'uu');
      const hasUuIframe = headers.some(
        (header) => header.field === 'uu_iframe',
      );
      const hasInmapPv = headers.some((header) => header.field === 'inmap_pv');
      const hasInmapPvIframe = headers.some(
        (header) => header.field === 'inmap_pv_iframe',
      );
      const hasInmapUu = headers.some((header) => header.field === 'inmap_uu');
      const hasInmapUuIframe = headers.some(
        (header) => header.field === 'inmap_uu_iframe',
      );

      const array = (Object.entries(allarea.pv) as [string, number][])
        .filter((el) => el.length > 1)
        .sort((a, b) => {
          if (Number.isNaN(a[1]) && Number.isNaN(b[1])) {
            return 0;
          }
          if (Number.isNaN(a[1])) {
            return 1;
          }
          if (Number.isNaN(b[1])) {
            return -1;
          }
          return b[1] - a[1];
        })
        .map(([key, pv]) => {
          const key_formatted = key.replaceAll('\n', ' ');
          const row = [key_formatted, String(pv)];
          if (hasPvIframe) {
            row.push(String(allarea.pv_iframe[key] || 0));
          }
          if (hasUu) {
            row.push(String(allarea.uu[key] || 0));
          }
          if (hasUuIframe) {
            row.push(String(allarea.uu_iframe[key] || 0));
          }
          if (hasInmapPv) {
            row.push(String(inmap.pv[key] || 0));
          }
          if (hasInmapPvIframe) {
            row.push(String(inmap.pv_iframe[key] || 0));
          }
          if (hasInmapUu) {
            row.push(String(inmap.uu[key] || 0));
          }
          if (hasInmapUuIframe) {
            row.push(String(inmap.uu_iframe[key] || 0));
          }
          return row;
        });

      return array;
    }

    // v2micampaign の場合
    // allareaの直下に、{cp, md, sc, pv, uu}を配列で持つ形式
    if (isFsV2DataCampaign(data)) {
      const campaignData = this.fsService.sumV2CampaignData(data);
      const campaignDataSorted = Object.entries(campaignData)
        .sort((a, b) => b[1].pv - a[1].pv)
        .map((a) => a[1]);

      const array = campaignDataSorted.map((item: CampaignDataItem) => {
        const { cp, md, sc, pv, uu } = item;
        const row = [cp, md, sc, String(pv || 0), String(uu || 0)];
        return row;
      });
      return array;
    }

    // v2spots の場合
    if (isFsV2DataSpot(data)) {
      const mapID = this.dataService.getMapID();
      const spotData = await this.fsService.sumV2SpotData(data, mapID);

      const langs = getPreferredLangs();

      const { allarea, inmap } = spotData;

      // allareaとinmapの両方のスポットIDを統合
      const allSpotIds = new Set([
        ...Object.keys(allarea),
        ...Object.keys(inmap),
      ]);

      const spotDataSorted = Array.from(allSpotIds)
        .map((id) => {
          const allareaItem = allarea[id];
          const inmapItem = inmap[id];

          return {
            id,
            name: allareaItem?.name || inmapItem?.name,
            sv: allareaItem?.sv || 0,
            inmap_sv: inmapItem?.sv || 0,
          };
        })
        .sort((a, b) => b.sv - a.sv);

      const array = spotDataSorted.map(
        (item: {
          id: string;
          name: LangMap | undefined;
          sv: number;
          inmap_sv: number;
        }) => {
          const { name, sv, inmap_sv } = item;
          const row = [
            resolveLangMapValue(name, langs),
            String(sv || 0),
            String(inmap_sv || 0),
          ];
          return row;
        },
      );

      return array;
    }

    return [];
  }
}
